import PropTypes from 'prop-types'
import clsx from 'clsx'

import { AUDIENCE_ORDER } from './content'
import { SlideMeta, markup } from './Slide'
import styles from './style.module.scss'

/*
 * Zwei Fassungen derselben Sache nebeneinander.
 *
 * Gebaut für die Folie „Dasselbe Problem, zweimal gemeldet" — und aus einem
 * Grund, der beim ersten Versuch zu spät auffiel: Dort standen beide
 * Meldungen in den gewöhnlichen Spalten „Was es tut" / „Woran es hängt", also
 * als zwei Listen aus gleich aussehenden Punkten. Meldungstext und die
 * Rückfragen, die daraus folgen, trugen denselben Aufzählungspunkt — es sah
 * aus, als gehöre alles zu **einer** Meldung, und die Gegenüberstellung, um
 * die es eigentlich geht, war gar nicht zu erkennen.
 *
 * Jetzt sind es zwei Karten mit eigenem Rahmen und eigener Farbe, und in jeder
 * sind drei Dinge sichtbar getrennt:
 *
 *   1. der **Betreff** — abgesetzt wie eine Titelzeile,
 *   2. der **Meldungstext** — als Zitat, damit erkennbar ist, dass das der
 *      Wortlaut ist und nicht unsere Erklärung dazu,
 *   3. **was daraus folgt** — mit eigener Überschrift und kleinerer Schrift.
 *
 * Die Farbe trägt die Aussage nicht allein: Jede Karte hat eine Kopfzeile in
 * Worten („So kommt sie zurück" / „So wird sie gelöst"). Wer Farben nicht
 * unterscheidet, verliert damit nichts.
 */

function Karte({ seite, ton }) {
	return (
		<section className={clsx(styles.vergleichKarte, ton === 'stark' ? styles.karteStark : styles.karteSchwach)}>
			<h3 className={styles.karteKopf}>{seite.label}</h3>

			<p className={styles.karteBetreff}>
				<span className={styles.karteFeld}>Titel</span>
				{seite.betreff}
			</p>

			<blockquote className={styles.karteText}>{seite.text}</blockquote>

			<p className={styles.karteFolgenTitel}>{seite.folgenTitel}</p>
			<ul className={styles.karteFolgen}>
				{seite.folgen.map(eintrag => (
					<li key={eintrag} dangerouslySetInnerHTML={{ __html: markup(eintrag) }} />
				))}
			</ul>
		</section>
	)
}

Karte.propTypes = { seite: PropTypes.object.isRequired, ton: PropTypes.string }

export default function CompareSlide({ slide }) {
	return (
		<div className={styles.slide}>
			<header className={styles.head}>
				<div>
					{slide.kicker && <p className={styles.kicker}>{slide.kicker}</p>}
					<h2 className={styles.title}>{slide.title}</h2>
				</div>
				<SlideMeta audience={slide.audience} priority={slide.priority} />
			</header>

			<p className={styles.goal}>{slide.goal}</p>

			<div className={styles.body}>
				<div className={styles.vergleich}>
					<Karte seite={slide.links} ton="schwach" />
					<Karte seite={slide.rechts} ton="stark" />
				</div>
			</div>

			{slide.watch && (
				<p className={styles.watch}>
					<span className={styles.watchLabel}>Aufpassen</span>
					{slide.watch}
				</p>
			)}
		</div>
	)
}

CompareSlide.propTypes = {
	slide: PropTypes.shape({
		title: PropTypes.string.isRequired,
		kicker: PropTypes.string,
		goal: PropTypes.string,
		audience: PropTypes.arrayOf(PropTypes.oneOf(AUDIENCE_ORDER)),
		priority: PropTypes.object,
		links: PropTypes.object.isRequired,
		rechts: PropTypes.object.isRequired,
		watch: PropTypes.string
	}).isRequired
}
